import React, { useEffect, useState } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';

import {
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
} from '@mui/material';
import { createColumnHelper, flexRender } from '@tanstack/react-table';
import { ChevronDown, ChevronUp } from 'lucide-react';

import Button from '../../common/Button';
import Loading from '../../common/Loading';
import { createTanstackTable, toastPromise } from '../../common/utils';
import { useGetModelById, useUpdateModel } from '../../hooks';
import { useGetModelOutput } from '../../hooks/tanstack/useModelsHook';
import { useGetRouteParams } from '../../hooks/tanstack/useUtilsHook';
import MenuDropdown from '../MenuDropdown';

type DriverType =
  | 'aircraft_block_hours'
  | 'crew_credit_hours'
  | 'crew_duty_days';

export type DataRow = {
  aircraft_type: string;
  airport_code: string;
  crew_type: string;
  driver: DriverType;
};

const columnHelper = createColumnHelper<DataRow>();

const columns = [
  columnHelper.accessor('aircraft_type', {
    cell: (info) => {
      const value = info.getValue();

      return (
        <div className="flex w-full items-center justify-between">
          <div className="flex flex-col py-2">
            <p className="font-semibold">{value}</p>
          </div>
        </div>
      );
    },
    header: () => (
      <div className="flex items-center gap-1">
        <p>Aircraft</p>
      </div>
    ),
  }),
  columnHelper.accessor('airport_code', {
    cell: (info) => {
      const value = info.getValue();

      return (
        <div className="flex w-full items-center justify-between">
          <div className="flex flex-col py-2">
            <p className="font-semibold">{value}</p>
          </div>
        </div>
      );
    },
    header: () => (
      <div className="flex items-center gap-1">
        <p>Base</p>
      </div>
    ),
  }),
  columnHelper.accessor('crew_type', {
    cell: (info) => {
      const value = info.getValue();

      return (
        <div className="flex w-full items-center justify-between">
          <div className="flex flex-col py-2">
            <p className="font-semibold">{value}</p>
          </div>
        </div>
      );
    },
    header: () => (
      <div className="flex items-center gap-1">
        <p>Crew Rank</p>
      </div>
    ),
  }),
  columnHelper.accessor('driver', {
    cell: (info) => {
      const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
      const open = Boolean(anchorEl);

      const { setValue, watch } = useFormContext();

      const aircraft = info.row.original.aircraft_type;
      const airport = info.row.original.airport_code;
      const crewRank = info.row.original.crew_type;

      const driver =
        watch(`${aircraft}.${airport}.${crewRank}.driver`) ??
        info.row.original.driver;

      const options = [
        {
          label: 'Aircraft Block Hours',
          value: 'Aircraft Block Hours',
        },
        {
          label: 'Crew Credit Hours',
          value: 'Crew Credit Hours',
        },
        {
          label: 'Crew Duty Days',
          value: 'Crew Duty Days',
        },
      ];

      const handleClose = () => setAnchorEl(null);

      const handleSelectDriver = (id: string) => {
        setValue(`${aircraft}.${airport}.${crewRank}.driver`, id);
        handleClose();
      };

      return (
        <div className="flex w-full items-center justify-between">
          <MenuDropdown
            anchorEl={anchorEl}
            setAnchorEl={setAnchorEl}
            open={open}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            trigger={
              <div className="flex justify-between w-full gap-2 text-[#666666]">
                <p className="font-semibold w-full text-start whitespace-normal">
                  {options.find((o) => o.value === driver)?.label}
                </p>
                {open ? <ChevronUp width={15} /> : <ChevronDown width={15} />}
              </div>
            }
            style={{
              width: '100%',
              border: 'none !important',
              top: '10px !important',
            }}
            rootClassName="w-full"
            triggerClassName="rounded-md w-full"
          >
            <Divider
              style={{
                margin: 0,
              }}
            />
            {options?.map((o) => (
              <MenuItem
                defaultChecked={o.value === driver}
                key={o.value}
                onClick={() => handleSelectDriver(o.value)}
                className="flex transition hover:bg-slate-200 p-2 cursor-pointer w-full h-full gap-2"
              >
                {o.label}
              </MenuItem>
            ))}
          </MenuDropdown>
        </div>
      );
    },
    header: () => (
      <div className="flex items-center gap-1">
        <p>Driver</p>
      </div>
    ),
  }),
];

type ConfigureDriverDialogProps = {
  open: boolean;
  handleClose: () => void;
  isLoading: boolean;
};

const ConfigureDriverDialog = ({
  open,
  handleClose,
  isLoading,
}: ConfigureDriverDialogProps) => {
  const [data, setData] = useState<DataRow[]>([]);

  const { modelId, orgId, projectId } = useGetRouteParams();

  const table = createTanstackTable<DataRow>({
    data,
    columns,
  });

  const form = useForm();

  const { setValue, watch } = form;

  const formData = watch();

  const { data: model } = useGetModelById(
    {
      modelId,
      orgId,
      projectId,
    },
    {
      enabled: !!projectId && !!modelId && !!orgId,
    }
  );

  const { refetch: refetchModelOutput } = useGetModelOutput(
    {
      modelId,
      orgId,
      projectId,
    },
    {
      enabled: !!projectId && !!modelId && !!orgId,
    }
  );

  const { mutateAsync: updateModel, isPending: isUpdatingModel } =
    useUpdateModel({
      onSuccess: () => {
        refetchModelOutput();
      },
    });

  useEffect(() => {
    if (!model?.driver_config) {
      return;
    }

    model.driver_config.forEach((row) => {
      setValue(
        `${row.aircraft_type}.${row.airport_code}.${row.crew_type}.driver`,
        row.driver
      );
    });

    setData(model.driver_config as DataRow[]);
  }, [model?.driver_config]);

  const onSubmit = async () => {
    const res = updateModel({
      orgId,
      projectId,
      modelId,
      data: {
        driver_config: data.map((row) => {
          const driver =
            formData[row.aircraft_type][row.airport_code][row.crew_type][
              'driver'
            ];

          return {
            ...row,
            driver,
          };
        }),
      },
    });

    await res;

    handleClose();

    toastPromise({
      promise: res,
      content: 'Driver configuration updated successfully',
    });
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (isUpdatingModel) {
          return;
        }
        handleClose();
      }}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <h1 className="font-semibold">Driver Configuration</h1>
      </DialogTitle>
      <DialogContent>
        <FormProvider {...form}>
          <div className="w-full space-y-4">
            <table className="w-full font-normal">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr
                    key={headerGroup.id}
                    className="!rounded-md border-[#E4E7EC] bg-[#F8F8F8] first:border-l last:border-r"
                  >
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className={
                          'first:border-l-2 border-t-2 border-b-2 last:border-r-2 border-[#E4E7EC] px-6 py-3 text-start text-sm text-white bg-[#01285F]'
                        }
                      >
                        <button
                          type="button"
                          onClick={header.column.getToggleSortingHandler()}
                          className="flex gap-1 items-start"
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </button>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="relative h-full">
                {isLoading && <Loading className="h-full absolute" />}
                {table.getRowModel().rows?.map((row) => (
                  <tr
                    key={row.id}
                    className="cursor-pointer px-6 py-3 transition"
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className="border-b border-[#E4E7EC] px-6 text-[#333] first:border-l last:border-r"
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {!table.getRowModel().rows.length && !isLoading && (
              <div className="flex justify-center border border-t-0 border-[#E4E7EC] py-20">
                <p className="text-lg text-[#999] font-normal">
                  Data is empty, make sure to click run first.
                </p>
              </div>
            )}
            <div className="flex-end flex justify-end gap-3">
              <Button
                onClick={handleClose}
                variant="outlined"
                tabIndex={3}
                disabled={isUpdatingModel}
              >
                Cancel
              </Button>
              <Button
                loading={isUpdatingModel}
                tabIndex={2}
                type="button"
                onClick={() => onSubmit()}
                variant="contained_blue"
              >
                Save
              </Button>
            </div>
          </div>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
};

export default ConfigureDriverDialog;
